import {
	mergeStyles,
	ScrollablePaneCompat as ScrollablePane,
	SelectionMode,
} from 'components/FluentUICompat';
import {EntityList, EntityListColumn, EntityListProps} from 'components';
import {ConfirmDeleteDialog, useDialogState} from 'components/Dialogs';
import {useCommand, useSelection} from 'hooks';
import React from 'react';
import {useEntityContext} from './EntityContext';
import {UserRole} from 'types';
import {
	ExtraFieldsForNameColumn,
	useEntityNameColumn,
} from './useEntityNameColumn';
import {useEditEntityCmd} from 'hooks/useEditEntityCmd';
import {EntityFormPanels, EntityFormPanelsProps} from './EntityFormPanels';
import {EntityOfFormPanels} from './EntityFormPanels.types';
import {
	StateForEntityFormPanels,
	useStateForEntityFormPanels,
	useEntityPageTranslation,
} from './hooksForEntityFormPanels';
import {FormMode} from './EntityPage.types';
import {useBoolean} from '@fluentui/react-hooks';
import {EntityListDragDrop} from 'components/EntityList/EntityListDragDrop';

type KeysToOmitFromPanelProps<T extends EntityOfFormPanels> =
	| 'performExtraTaskOnCancellation'
	| 'infoOfUpdateDialog'
	| 'entity'
	| keyof StateForEntityFormPanels<T>;

/**
 * * Note
 *
 * Use propsOfEntityFormPanel from now on because we might remove this in the
 * future.
 */
type PropsFromPanels<T extends EntityOfFormPanels> = Omit<
	EntityFormPanelsProps<T>,
	KeysToOmitFromPanelProps<T>
>;

export type EntityPageCustomTranslations = {
	deleteSubtextTranslationKey?: string;
	deleteManySubtextTranslationKey?: string;
	createHeaderTranslationKey?: string;
	editHeaderTranslationKey?: string;
};

export interface EntityPageProps<T extends EntityOfFormPanels>
	extends PropsFromPanels<T> {
	items: T[];
	deleteEntity: (id: string) => any;
	onDeleteMany?: (ids: string[]) => any;
	additionalColumns?: EntityListColumn[];
	onViewItem?: (item: T) => void;
	disableDelete?: (selectedItem: T) => boolean;
	disableDeleteMany?: (selectedItems: T[]) => boolean;
	disableEdit?: (selectedItem: T) => boolean;
	onShouldVirtualize?: () => boolean;
	groupListByFieldName?: string;
	hideCreate?: boolean;
	hideDelete?: boolean;
	hideDeleteMany?: boolean;
	hideEdit?: boolean;
	hideSortPermanent?: boolean;
	rolesCreate?: UserRole[];
	rolesEdit?: UserRole[];
	rolesDelete?: UserRole[];
	/**
	 * If your page has content above the Entity Page, you might need to set this
	 * to false in some cases to prevent a double vertical scrollbar.
	 */
	shouldWrapListWithScrollablePane?: boolean;
	entityListProps?: Partial<EntityListProps>;
	propsOfEntityFormPanel?: Partial<EntityFormPanelsProps<T>>;
	withNameColumnInPanel?: boolean;
	extraFieldsForNameColumn?: ExtraFieldsForNameColumn;
	className?: string;
	customTranslations?: EntityPageCustomTranslations;
	editCommandToolTip?: (selectedItem: T) => string;
}

const userRoles = [
	UserRole.SystemAdministrator,
	UserRole.ServiceProvider,
	UserRole.Vex,
	UserRole.VexSupport,
	UserRole.Vko,
	UserRole.VkoExternal,
	UserRole.ShApprover,
	UserRole.Readers,
	UserRole.RegulationReader,
	UserRole.Bav,
	UserRole.Avk,
];

/**
 * @see EntityList for some important notes about using this component.
 */
export function EntityPage<T extends EntityOfFormPanels>(
	props: EntityPageProps<T>,
) {
	const {t} = useEntityPageTranslation();
	const {
		items,
		entityDisplayName,
		deleteEntity,
		additionalColumns = [],
		onViewItem,
		disableDelete = () => false,
		disableEdit = () => false,
		onShouldVirtualize = () => false,
		withNameColumn = true,
		withNameColumnInPanel,
		groupListByFieldName,
		hideCreate = false,
		hideDelete = false,
		hideEdit = false,
		hideSortPermanent = true,
		rolesCreate = userRoles,
		rolesEdit = userRoles,
		rolesDelete = userRoles,
		shouldWrapListWithScrollablePane = true,
		entityListProps,
		extraFieldsForNameColumn,
		propsOfEntityFormPanel,
		customTranslations,
		className,
		editCommandToolTip = () => '',
		...other
	} = props;

	const stateForPanels: StateForEntityFormPanels<T> =
		useStateForEntityFormPanels();
	const {
		modeInfo: [_mode, setMode],
		formInfo: {reset},
	} = stateForPanels;

	const [dragDropSortActivated, {toggle: toggleDragDropSort}] =
		useBoolean(false);
	const [selectedItem, setSelectedItem] = React.useState<T | undefined>();
	const {setSelectedItems} = useEntityContext<T>();
	const [selection] = useSelection<T>({
		onSelectionChanged(selectedItems) {
			setSelectedItem(selectedItems[0]);
			setSelectedItems?.(selectedItems);
		},
	});
	const [deleteDialogState, showDeleteDialog, hideDeleteDialog] =
		useDialogState();

	const handleDeleteClick = React.useCallback(() => {
		if (selectedItem) {
			deleteEntity(selectedItem.id);
		}

		hideDeleteDialog();
	}, [selectedItem, hideDeleteDialog]);

	useCommand(
		{
			key: 'view',
			text: t('View'),
			iconProps: {iconName: 'View'},
			priority: 1,
			disabled: !selectedItem,
			hidden: !onViewItem,
			onClick: () => selectedItem && onViewItem?.(selectedItem),
		},
		[selectedItem],
	);
	useCommand(
		{
			key: 'new',
			text: t('New'),
			iconProps: {iconName: 'Add'},
			priority: 2,
			disabled: Boolean(selectedItem),
			onClick() {
				reset({} as any);
				setMode(FormMode.Create);
			},
			hidden: hideCreate,
			roles: rolesCreate,
		},
		[selectedItem, hideCreate, rolesCreate],
	);

	useEditEntityCmd(
		{
			priority: 3,
			disabled: !selectedItem || disableEdit(selectedItem),
			key: 'edit',
			onClick() {
				reset(selectedItem ?? ({} as any));
				// eslint-disable-next-line no-unused-expressions
				(selectedItem as any).status === 'FINAL'
					? setMode(FormMode.Create)
					: setMode(FormMode.Update);
			},
			hidden: hideEdit,
			roles: rolesEdit,
			title: editCommandToolTip ? editCommandToolTip(selectedItem as T) : '',
		},
		[selectedItem, hideEdit, rolesEdit],
	);

	useCommand(
		{
			key: 'delete',
			text: t('Delete'),
			iconProps: {iconName: 'Delete'},
			priority: 4,
			disabled: !selectedItem || disableDelete(selectedItem),
			onClick() {
				showDeleteDialog();
			},
			hidden: hideDelete,
			roles: rolesDelete,
		},
		[selectedItem, hideDelete, rolesDelete],
	);

	useCommand(
		{
			key: 'dragDropSort',
			text: t('DragDropSort', {
				keyPrefix: 'EntityPageSort',
			}),
			iconProps: {iconName: 'Sort'},
			priority: 5,
			onClick: toggleDragDropSort,
			hidden: hideSortPermanent || dragDropSortActivated,
		},
		[dragDropSortActivated],
	);

	const nameColumn: EntityListColumn = useEntityNameColumn(
		entityDisplayName,
		extraFieldsForNameColumn,
	);

	const columns: EntityListColumn[] = React.useMemo(
		() =>
			withNameColumn
				? [nameColumn, ...additionalColumns]
				: [...additionalColumns],
		[],
	);

	const onItemInvoked = React.useCallback((item: T) => onViewItem?.(item), []);

	const renderEntityList = (): React.ReactElement => {
		return dragDropSortActivated ? (
			<EntityListDragDrop
				columns={columns}
				dragDropSortActivated={dragDropSortActivated}
				toggleDragDropSort={toggleDragDropSort}
				{...props}
			/>
		) : (
			<EntityList
				items={items}
				columns={columns}
				selectionMode={SelectionMode.single}
				selection={selection}
				onItemInvoked={onItemInvoked}
				onShouldVirtualize={onShouldVirtualize}
				groupByFieldName={groupListByFieldName}
				sticky={entityListProps?.sticky ?? true}
				{...entityListProps}
			/>
		);
	};

	const renderListWithScrollablePaneIfNecessary = (): React.ReactElement => {
		const list: React.ReactElement = renderEntityList();
		if (shouldWrapListWithScrollablePane)
			return <ScrollablePane>{list}</ScrollablePane>;
		return list;
	};

	const list: React.ReactElement = renderListWithScrollablePaneIfNecessary();

	const getClassName = (): string => {
		return mergeStyles([wrapperClassName, className]);
	};

	return (
		<div className={getClassName()}>
			{list}
			<EntityFormPanels
				entityDisplayName={entityDisplayName}
				entity={selectedItem}
				performExtraTaskOnCancellation={hideDeleteDialog}
				withNameColumn={withNameColumnInPanel ?? withNameColumn}
				customTranslations={customTranslations}
				{...stateForPanels}
				{...other}
				{...propsOfEntityFormPanel}
			/>
			<ConfirmDeleteDialog
				{...deleteDialogState}
				onConfirm={handleDeleteClick}
			/>
		</div>
	);
}

const wrapperClassName = mergeStyles({
	height: '100%',
	position: 'relative',
});
